import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

const AlertWrapper = styled.div`
  display: ${({ active }) => (active === "true" ? "flex" : "none")};
  margin-bottom: 1.5rem;
  opacity: ${({ active }) => (active === "true" ? "1" : "0")};
  transition: opacity 0.5s;
  flex-wrap: wrap;

  &.is-sticky {
    position: fixed;
    top: 10px;
    z-index: 999;
    animation: 500ms ease-in-out 0s normal none 1 running fadeInDown;
  }

  @media (max-width: ${({ theme }) => theme.maxMobileWidth}) {
    margin-left: 0.25rem;
    margin-right: 0.25rem;
  }
`;

const Alert = ({ children, color, className = '', shouldStick = false }) => {
    const alertRef = useRef(null)
    const [alertElementTop, setAlertElementTop] = useState(undefined);
    
    useEffect(() => {
        try {
            const alertElementEl = alertRef?.current.getBoundingClientRect();
            setAlertElementTop(alertElementEl.top);
        } catch {}
    }, []);

    const isSticky = useCallback((e) => {
        const alertElementEl = alertRef?.current;
        const scrollTop = window.scrollY;
        if (scrollTop >= alertElementEl.getBoundingClientRect()?.top - 10) {
            const cs = window.getComputedStyle(alertElementEl.parentElement, null)
            const width = cs.getPropertyValue('width')
            const paddingLeft = cs.getPropertyValue('padding-left')
            const paddingRight = cs.getPropertyValue('padding-right')
            alertElementEl.style.width = `calc(${width} - ${paddingLeft} - ${paddingRight})`
            alertElementEl && alertElementEl.classList.add('is-sticky');
        } else {
            alertElementEl && alertElementEl.classList.remove('is-sticky');
        }
    }, [])
    
    useEffect(() => {
        if (alertElementTop === undefined || !shouldStick) return;
        
        window.addEventListener('scroll', isSticky);
        return () => {
            window.removeEventListener('scroll', isSticky);
        };
    }, [alertElementTop, isSticky, shouldStick]);

    const RenderAlertChildren = ({ content = null }) => {
        if (Array.isArray(content)) {
            return content.map(c => {
                // empty node needs to be rendered as space
                if (c.trim() === '') {
                    return '&nbsp;'
                }
                return c
            }).map((c, id) => (
                <RenderAlertChildren content={c} key={id} />
            ))
        }
        // If the content is a plain string, render as is;
        // otherwise render as HTML (e.g. translated strings that contains foreign characters)
        return content?.props?.children
            ? content
            : <div dangerouslySetInnerHTML={{__html: content}} />
    }

    return (
        <AlertWrapper
            active={String(!!children)}
            className={`alert alert-${color} ${className}`}
            role="alert"
            data-mdb-color={color}
            ref={alertRef}
        >
            <RenderAlertChildren content={children} />
        </AlertWrapper>
    )
};

export default Alert;
